import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import * as cognito from './cognito';

type AuthContextValue = {
  session: cognito.AuthSession | null;
  email: string | null;
  /** Nome o email per intestazioni. */
  displayName: string;
  /** Avatar da id token (`custom:avatar`), se presente. */
  avatarUrl: string | null;
  setSession: (s: cognito.AuthSession | null) => void;
  signOut: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function parseIdTokenClaims(idToken: string | undefined): {
  email: string | null;
  displayName: string;
  avatarUrl: string | null;
} {
  if (!idToken) return { email: null, displayName: '', avatarUrl: null };
  try {
    const part = idToken.split('.')[1];
    if (!part) return { email: null, displayName: '', avatarUrl: null };
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const p = JSON.parse(json) as Record<string, string>;
    const email = p.email || p['cognito:username'] || null;
    const name = (p.name || '').trim();
    const displayName = name || email || '?';
    const av = (p['custom:avatar'] || '').trim();
    const avatarUrl =
      av && (av.startsWith('http') || av.startsWith('data:')) ? av : null;
    return { email, displayName, avatarUrl };
  } catch {
    return { email: null, displayName: '', avatarUrl: null };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<cognito.AuthSession | null>(() => cognito.loadSession());

  useEffect(() => {
    setSession(cognito.loadSession());
  }, []);

  const { email, displayName, avatarUrl } = useMemo(
    () => parseIdTokenClaims(session?.idToken),
    [session?.idToken]
  );

  const signOut = useCallback(() => {
    cognito.clearSession();
    setSession(null);
  }, []);

  const value = useMemo(
    () => ({ session, email, displayName, avatarUrl, setSession, signOut }),
    [session, email, displayName, avatarUrl, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth fuori da AuthProvider');
  return ctx;
}
